import type { ListBuilderConfiguration } from '../types';

/**
 * Execution status value mapping
 */
const EXECUTION_STATUS_VALUE_MAPPING = [
	{
		value: 'batch_pending',
		label: 'Queued',
		color: { bg: '#e5e7eb', text: '#374151' },
	},
	{
		value: 'pending',
		label: 'Pending',
		color: { bg: '#fef3c7', text: '#92400e' },
	},
	{
		value: 'active',
		label: 'Active',
		color: { bg: '#dbeafe', text: '#1e40af' },
	},
	{
		value: 'completed',
		label: 'Completed',
		color: { bg: '#dcfce7', text: '#166534' },
	},
	{
		value: 'failed',
		label: 'Failed',
		color: { bg: '#fee2e2', text: '#991b1b' },
	},
	{
		value: 'paused',
		label: 'Paused',
		color: { bg: '#fef3c7', text: '#92400e' },
	},
	{
		value: 'batch_cancelled',
		label: 'Cancelled',
		color: { bg: '#fecaca', text: '#7f1d1d' },
	},
	{
		value: 'excluded',
		label: 'Excluded',
		color: { bg: '#d1d5db', text: '#4b5563' },
	},
];

/**
 * Automation Reports list configuration
 * Converted from JSON to TypeScript for type safety
 */
export const AUTOMATION_REPORTS_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-automation-reports',
			cacheTTL: 600000,
			name: 'All Executions',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'execution-id',
					headers: [{ id: 'execution-id-header', text: 'Execution' }],
					fields: [
						{
							id: 'id-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'id',
									objectTable: 'automation_execution',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'text',
							prefix: '#',
							link: {
								type: 'object',
								enabled: true,
							},
						},
					],
					sortable: true,
					sortFields: [
						{
							objectTable: '_automation_execution',
							fieldName: 'id',
							direction: 'DESC',
						},
					],
					width: 100,
				},
				{
					id: 'flow-name',
					headers: [{ id: 'flow-name-header', text: 'Flow' }],
					fields: [
						{
							id: 'flow-name-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'version.flow.name',
									objectTable: 'automation_execution',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
							link: {
								type: 'custom',
								enabled: true,
								urlTemplate:
									'/automation/flows/{version.flow.id}/version/{version.id}',
							},
						},
						{
							id: 'line-break-1',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							displayStyle: 'line_break',
						},
						{
							id: 'version-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'version.name',
									objectTable: 'automation_execution',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'text',
							emptyValue: '',
						},
					],
					sortable: true,
					sortFields: [
						{
							objectTable: '_automation_version',
							fieldName: '_fk__automation_flow__id',
							direction: 'ASC',
						},
					],
					minWidth: 200,
				},
				{
					id: 'status',
					headers: [{ id: 'status-header', text: 'Status' }],
					fields: [
						{
							id: 'status-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'executionStatus',
									objectTable: 'automation_execution',
								},
							},
							styleConfig: {
								size: 'sm',
								valueMapping: EXECUTION_STATUS_VALUE_MAPPING,
							},
							displayStyle: 'badge',
						},
					],
					sortable: true,
					sortFields: [
						{
							objectTable: '_automation_execution',
							fieldName: 'executionStatus',
							direction: 'ASC',
						},
					],
					width: 120,
				},
				{
					id: 'trigger',
					headers: [{ id: 'trigger-header', text: 'Trigger' }],
					fields: [
						{
							id: 'trigger-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'trigger.name',
									objectTable: 'automation_execution',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'text',
							emptyValue: 'Manual',
						},
					],
					width: 150,
				},
				{
					id: 'batch',
					headers: [{ id: 'batch-header', text: 'Batch' }],
					fields: [
						{
							id: 'batch-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'batch.name',
									objectTable: 'automation_execution',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'text',
							emptyValue: '-',
						},
					],
					width: 150,
				},
				{
					id: 'entity',
					headers: [{ id: 'entity-header', text: 'Entity' }],
					fields: [
						{
							id: 'entity-id-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'entityId',
									objectTable: 'automation_execution',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
							emptyValue: '-',
						},
						{
							id: 'entity-line-break',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							displayStyle: 'line_break',
						},
						{
							id: 'entity-type-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'entityType',
									objectTable: 'automation_execution',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'text',
							emptyValue: '',
						},
					],
					width: 120,
				},
				{
					id: 'dedup-key',
					headers: [{ id: 'dedup-key-header', text: 'Dedup Key' }],
					fields: [
						{
							id: 'dedup-key-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'deduplicationKey',
									objectTable: 'automation_execution',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'text',
							emptyValue: '-',
						},
					],
					width: 150,
				},
				{
					id: 'started-at',
					headers: [{ id: 'started-at-header', text: 'Started' }],
					fields: [
						{
							id: 'started-at-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'startedAt',
									objectTable: 'automation_execution',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'muted' },
								formatOptions: { dateFormat: 'datetime-short' },
							},
							displayStyle: 'date',
						},
					],
					sortable: true,
					sortFields: [
						{
							objectTable: '_automation_execution',
							fieldName: 'startedAt',
							direction: 'DESC',
						},
					],
					width: 150,
				},
				{
					id: 'duration',
					headers: [{ id: 'duration-header', text: 'Duration' }],
					fields: [
						{
							id: 'duration-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'duration',
									objectTable: 'automation_execution',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'text',
							emptyValue: '-',
						},
					],
					width: 100,
				},
			],
			defaultSortId: 'started-at-desc',
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 20,
		pageSizeOptions: [10, 20, 50, 100],
	},
	filters: {
		activeFilters: [
			'automationExecutionReportKeywords',
			'automationExecutionReportStatus',
			'automationExecutionReportCreatedDate',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/automation/executions/:id',
			objectLabel: 'Execution',
			objectTable: '_automation_execution_report',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'executionStatus', fieldLabel: 'Status' },
				{ fieldName: 'entityType', fieldLabel: 'Entity Type' },
				{ fieldName: 'entityId', fieldLabel: 'Entity ID' },
				{ fieldName: 'deduplicationKey', fieldLabel: 'Dedup Key' },
				{ fieldName: 'startedAt', fieldLabel: 'Started At' },
				{ fieldName: 'completedAt', fieldLabel: 'Completed At' },
				{ fieldName: 'errorMessage', fieldLabel: 'Error Message' },
			],
		},
	],
};
